<template>
    <div class="container form-container p-4">
        <div class="text-3xl mb-4 pb-2 font-light flex items-end justify-between">
            <div data-cy="admin-migration-title">
                {{ $t('admin.migration.list.title') }}
            </div>
        </div>
        <migration-table ref="MigrationTable" @is-form-dirty="isFormDirty" @onTableAction="onTableAction" />
        <a-drawer
            data-cy="migration-drawer"
            :title="$t('admin.migration.drawer.title')"
            :width="500"
            :open="drawer_visible"
            :body-style="{ padding: '0px', paddingBottom: '10px' }"
            :destroyOnClose="true"
            :maskClosable="false"
            :rootClassName="'epic-' + moduleStateLink"
            @close="onDrawerClose"
        >
            <form-migration
                :migration="migration"
                :processing="processing"
                :validation="validation"
                @is-form-dirty="isFormDirty"
                @formSubmit="onSave"
                @onDrawerClose="onDrawerClose"
            >
                <template #actions>
                    <div data-cy="actions-list">
                        <a-dropdown
                            data-cy="actions-select"
                            :trigger="['click']"
                            class="float-left action-select"
                            v-if="migration.id"
                        >
                            <a-button @click="(e) => e.preventDefault()">
                                {{ $t('admin.migration.drawer.actions') }}
                                <DownOutlined />
                            </a-button>
                            <template #overlay>
                                <a-menu @click="onActionButtonsForm">
                                    <a-menu-item key="activities" data-cy="actions-activities">
                                        {{ $t('admin.migration.drawer.action.activities') }}
                                    </a-menu-item>
                                    <a-menu-item key="skips" data-cy="actions-skips">
                                        {{ $t('admin.migration.drawer.action.skips') }}
                                    </a-menu-item>
                                </a-menu>
                            </template>
                        </a-dropdown>
                    </div>
                </template>
            </form-migration>
        </a-drawer>

        <modal-migration-activity
            ref="ActivitiesModal"
            :migration_id="migration.id"
            :current_migration_status="current_migration_status"
        />
        <modal-migration-skip ref="SkipsModal" :migration_id="migration.id" />
    </div>
</template>

<script>
import FormMigration from '@/components/forms/Migration.vue';
import MigrationTable from '@/components/tables/Migration.vue';
import ModalMigrationActivity from '@/components/modals/MigrationActivity.vue';
import ModalMigrationSkip from '@/components/modals/MigrationSkip.vue';
import { DownOutlined } from '@ant-design/icons-vue';

export default {
    components: {
        FormMigration,
        MigrationTable,
        ModalMigrationActivity,
        ModalMigrationSkip,
        DownOutlined,
    },
    props: {
        is_form_dirty: {
            type: Boolean,
            default: false,
        },
    },
    data() {
        return {
            drawer_visible: false,
            migration: {},
            migration_id: 0,
            current_migration_status: 'pending',
            validation: {
                errors: {},
                message: '',
            },
            processing: false,
        };
    },
    computed: {
        moduleStateLink() {
            return this.$store.state.system.is_epicvault_enabled ? 'default' : 'claim';
        },
    },
    created() {
        this.startMigrationListener();
    },
    beforeDestroy() {
        this.stopMigrationListener();
    },
    methods: {
        startMigrationListener() {
            let that = this;

            this.migration_stream = new EventSource('/stream/migrations', { withCredentials: true });

            this.migration_stream.addEventListener(
                'message',
                (event) => {
                    let data = JSON.parse(event.data);

                    if (data.is_updated !== undefined && data.is_updated == true) {
                        this.$refs.MigrationTable.fetch();
                    }
                },
                false,
            );

            this.migration_stream.addEventListener(
                'error',
                (event) => {
                    if (event.readyState == EventSource.CLOSED) {
                        that.migration_stream.close();
                    }
                },
                false,
            );
        },
        stopMigrationListener() {
            this.migration_stream.close();
        },
        isFormDirty(is_form_dirty) {
            this.$emit('is-form-dirty', is_form_dirty);
        },
        onTableAction(data) {
            switch (data.key) {
                case 'view':
                    this.openDrawer({ id: data.id });
                    break;
                case 'stop':
                    this.onStopMigration(data.id);
                    break;
                case 'restart':
                    this.onRestartMigration(data.id);
                    break;
            }
        },
        onActionButtonsForm(event) {
            this.modal_visible = true;

            switch (event.key) {
                case 'activities':
                    this.$refs.ActivitiesModal.showModal();
                    break;
                case 'skips':
                    this.$refs.SkipsModal.showModal();
                    break;
            }
        },
        openDrawer(record) {
            if (!_.isEmpty(record) && record.id !== null) {
                this.migration_id = record.id;

                this.$http.get('/api/migrations/' + this.migration_id).then((response) => {
                    this.migration = {
                        ...response.data.data,
                    };
                    this.current_migration_status = this.migration.status;

                    this.drawer_visible = true;
                });
            } else {
                this.migration = {
                    id: 0,
                    migration_type: '4d',
                    migration_name: '',
                    claimsbank_ip: '',
                    claimsbank_port: '',
                    claimsbank_user: '',
                    claimsbank_password: '',
                    status: '',
                    status_updated_at: '',
                    migration_started_at: '',
                    migration_ended_at: '',
                };

                this.drawer_visible = true;
            }
        },
        onDrawerClose() {
            let can_close = true;

            if (this.is_form_dirty) {
                let that = this;
                can_close = false;

                this.$confirm({
                    title: this.$t('globals.navigation.notifications.unsaved_changes.title'),
                    content: this.$t('globals.navigation.notifications.unsaved_changes.content'),
                    onOk() {
                        that.isFormDirty(false);
                        that.drawer_visible = false;
                        that.migration_id = 0;
                        that.current_migration_status = 'pending';
                    },
                });
            }

            if (can_close) {
                this.isFormDirty(false);
                this.drawer_visible = false;
                this.migration_id = 0;
                this.current_migration_status = 'pending';
            }
        },
        onSave(data) {
            if (this.processing) {
                return false;
            }

            this.processing = true;

            let payload = data;

            this.$http
                .post('/api/migrations', payload)
                .then((response) => {
                    if (response.data.status == 'success') {
                        this.isFormDirty(false);

                        this.$refs.MigrationTable.sorter = {
                            field: 'created_at',
                            order: 'descend',
                        };

                        this.$refs.MigrationTable.reload();

                        this.drawer_visible = false;
                    } else {
                        this.$notification.error({
                            message: response.data.message,
                        });
                    }
                })
                .catch((error) => {
                    if (error.response.status == 422) {
                        this.validation = error.response.data;
                    } else if (error.response.status == 403) {
                        this.$notification.error({
                            message: this.$t('globals.global.notifications.error.403_mod'),
                        });
                    } else {
                        this.$notification.error({
                            message: this.$t('globals.global.notifications.error.any', { code: 'CVMGA01' }),
                        });
                    }
                })
                .finally(() => {
                    this.processing = false;
                });
        },
        onStopMigration(migration_id) {
            this.$http
                .post('/api/migrations/' + migration_id + '/stop')
                .then((response) => {
                    if (response.data.status == 'success') {
                        this.$notification.success({
                            message: response.data.message,
                        });

                        this.$refs.MigrationTable.reload();
                    } else {
                        this.$notification.error({
                            message: response.data.message,
                        });
                    }
                })
                .catch((error) => {
                    this.$notification.error({
                        message: this.$t('globals.global.notifications.error.any', { code: 'CVMGS01' }),
                    });
                });
        },
        onRestartMigration(migration_id) {
            this.$http
                .post('/api/migrations/' + migration_id + '/restart')
                .then((response) => {
                    if (response.data.status == 'success') {
                        this.$notification.success({
                            message: response.data.message,
                        });

                        this.$refs.MigrationTable.reload();
                    } else {
                        this.$notification.error({
                            message: response.data.message,
                        });
                    }
                })
                .catch((error) => {
                    this.$notification.error({
                        message: this.$t('globals.global.notifications.error.any', { code: 'CVMGR01' }),
                    });
                });
        },
        reload() {
            this.$refs.MigrationTable.reload();
        },
    },
};
</script>
